import ErrorPage from '../pages/ErrorPage';

interface NotFoundProps {
  requestedPath?: string;
  showBackButton?: boolean;
  showHomeButton?: boolean;
  dictionary?: any;
}

export default function NotFound({ 
  requestedPath,
  showBackButton = true,
  showHomeButton = true,
  dictionary
}: NotFoundProps) {
  
  const getCustomMessage = () => {
    if (requestedPath) {
      return `Lo sentimos, la página "${requestedPath}" no existe o ha sido movida.`;
    }
    return "Lo sentimos, la página que buscas no existe o ha sido movida.";
  };

  return (
    <ErrorPage
      errorCode="404"
      title={dictionary?.errorPages?.notFound?.title || "Página no encontrada"}
      message={getCustomMessage()}
      showBackButton={showBackButton}
      showHomeButton={showHomeButton}
    />
  );
}

// Componente para mostrar sugerencias específicas
export function NotFoundWithSuggestions({ requestedPath }: { requestedPath: string }) {
  const getSuggestions = () => {
    // Sugerencias basadas en la ruta solicitada
    if (requestedPath) {
      const path = requestedPath.toLowerCase();
      
      if (path.includes('servicio')) {
        return {
          title: "¿Buscabas nuestros servicios?",
          suggestions: [
            "Neuropsicología Infantil",
            "Psicología Infantil", 
            "Psicopedagogía",
            "Terapia de Lenguaje",
            "Terapia Ocupacional",
            // "Medicina General Infantil" // TEMPORALMENTE OCULTO
          ]
        };
      }
      
      if (path.includes('especialista')) {
        return {
          title: "¿Buscabas nuestras especialistas?",
          suggestions: [
            "Dr. Gabriel Méndez - Director",
            "Psicóloga Infantil",
            "Dra. Carolina Jiménez - Neuropsicóloga",
            "Dra. Laura Campos - Terapeuta",
            "Dra. Mónica Vargas - Psicopedagoga"
          ]
        };
      }
      
      if (path.includes('trastorno') || path.includes('condicion')) {
        return {
          title: "¿Buscabas información sobre trastornos?",
          suggestions: [
            "TDAH - Trastorno por Déficit de Atención",
            "Trastornos del Espectro Autista",
            "Trastornos del Aprendizaje",
            "Trastornos del Desarrollo",
            "Trastornos del Lenguaje"
          ]
        };
      }
    }
    
    return {
      title: "Páginas populares",
      suggestions: [
        "Inicio",
        "Nuestros Servicios",
        "Quiénes Somos",
        "Especialistas",
        "Contacto"
      ]
    };
  };

  const suggestions = getSuggestions();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-neuro-light-gray to-white flex items-center justify-center p-4">
      <div className="max-w-4xl mx-auto text-center">
        {/* Error 404 */}
        <div className="mb-12">
          <div className="text-9xl font-bold text-neuro-blue/20 mb-4">404</div>
          <h1 className="text-4xl md:text-6xl font-bold text-neuro-dark-blue mb-6">
            Página no encontrada
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Lo sentimos, la página "{requestedPath}" no existe.
          </p>
        </div>

        {/* Sugerencias personalizadas */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-neuro-dark-blue mb-6">
            {suggestions.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.suggestions.map((suggestion: string, index: number) => (
              <div 
                key={index}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <p className="text-gray-700 font-medium">{suggestion}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Volver atrás
          </button>
          
          <a
            href="/"
            className="px-6 py-3 bg-neuro-blue text-white rounded-lg hover:bg-neuro-dark-blue transition-colors"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}
